/**
 * Einstellungen › "Private Absender" (P2 hardening 04) — the caller's per-user sender-hide rows.
 *
 * Replaces the add-form side of "Meine Blockliste". E-Mails of these senders don't appear in the
 * caller's Posteingang but stay reachable in the Kommunikation. Colleagues are unaffected. The
 * legacy user-level blocklist rules (if any) are shown below by MyBlocklistSection as "alt".
 */

import { useState } from 'react';
import { EyeOff, Loader2 } from 'lucide-react';
import { isEmailAddress } from '@brokly/shared';
import { Button } from '../../components/ui/Button';
import { ApiError } from '../../lib/api-client';
import { dateShortDe } from '../../lib/format';
import {
  useSenderPrivateList,
  useMarkSenderPrivate,
  useUnmarkSenderPrivate,
} from './useSenderPrivate';
import { SenderPrivateUnmarkDialog } from './SenderPrivateDialog';

export function PrivateSendersSection() {
  const { data: mine = [], isLoading } = useSenderPrivateList();
  const mark = useMarkSenderPrivate();
  const unmark = useUnmarkSenderPrivate();
  const [value, setValue] = useState('');
  const [includePast, setIncludePast] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // An OPEN row still hides new AND (possibly) past mail → aufheben is the two-option dialog
  // (ab jetzt vs alle), not a blanket reveal_past:true. A CLOSED row only still hides past mail
  // → "Alle wieder anzeigen" is the single reveal-all action.
  const [unmarkRow, setUnmarkRow] = useState<{ id: string; address: string } | null>(null);

  function submit() {
    const address = value.trim();
    if (!isEmailAddress(address)) {
      setError('Bitte eine einzelne E-Mail-Adresse eingeben, z.B. name@firma.ch. Ganze Domains blockiert ein Admin.');
      return;
    }
    setError(null);
    mark.mutate(
      { address, include_past: includePast },
      {
        onSuccess: () => {
          setValue('');
          setIncludePast(false);
        },
        onError: (err) =>
          setError(err instanceof ApiError ? err.message : 'Absender konnte nicht markiert werden.'),
      },
    );
  }

  return (
    <section aria-labelledby="private-senders-title" className="flex flex-col gap-3 rounded-lg border bg-card p-4">
      <div>
        <h2 id="private-senders-title" className="flex items-center gap-2 text-sm font-medium">
          <EyeOff className="size-4" /> Private Absender
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          E-Mails dieser Absender erscheinen nicht in Ihrem Posteingang. Sie bleiben in der
          Kommunikation erreichbar. Kolleginnen und Kollegen sind nicht betroffen.
        </p>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Lade …</p>
      ) : mine.length === 0 ? (
        <p className="text-sm text-muted-foreground">Sie haben noch keine Absender privat markiert.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-border rounded-md border">
          {mine.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
              <span className="min-w-0 flex-1 truncate text-foreground">{r.address}</span>
              <span className="text-xs text-muted-foreground">seit {dateShortDe(r.marked_at)}</span>
              {r.hide_from === null && (
                <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                  alle bisherigen
                </span>
              )}
              {r.hide_until !== null && (
                <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                  bis {dateShortDe(r.hide_until)}
                </span>
              )}
              <Button
                size="sm"
                variant="outline"
                disabled={unmark.isPending}
                onClick={() =>
                  r.open
                    ? setUnmarkRow({ id: r.id, address: r.address })
                    : unmark.mutate({ id: r.id, reveal_past: true })
                }
                aria-label={`Private Markierung von ${r.address} aufheben`}
              >
                {r.open ? 'Nicht mehr privat' : 'Alle wieder anzeigen'}
              </Button>
            </li>
          ))}
        </ul>
      )}

      {unmarkRow && (
        <SenderPrivateUnmarkDialog
          address={unmarkRow.address}
          rowId={unmarkRow.id}
          onClose={() => setUnmarkRow(null)}
        />
      )}

      <form
        className="flex flex-col gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="flex items-start gap-2">
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="name@firma.ch"
            aria-label="Absender privat markieren"
            className="h-9 flex-1 rounded-md border border-border bg-background px-2.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <Button type="submit" disabled={mark.isPending || value.trim() === ''}>
            {mark.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
            Privat markieren
          </Button>
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input type="checkbox" checked={includePast} onChange={(e) => setIncludePast(e.target.checked)} />
          auch bisherige E-Mails ausblenden
        </label>
      </form>
      {error && (
        <p role="alert" className="text-xs text-status-danger">
          {error}
        </p>
      )}
    </section>
  );
}
